<template><section><h1>Box Sizing</h1>
<blockquote>
<p>允许您以特定的方式定义匹配某个区域的特定元素。</p>
</blockquote>
<table>
<thead>
<tr>
<th>class</th>
<th>属性</th>
</tr>
</thead>
<tbody>
<tr>
<td>.box-border</td>
<td>box-sizing: border-box;</td>
</tr>
<tr>
<td>.box-content</td>
<td>box-sizing: content-box;</td>
</tr>
</tbody>
</table>
<h2>包含 borders 和 padding</h2>
<p>使用 <code v-pre>box-border</code> 将元素的 <code v-pre>box-sizing</code> 设置为 <code v-pre>border-box</code>，告诉浏览器在给元素设置高度或宽度时包含元素的边框和填充。</p>
<p>这意味着一个 <code v-pre>100px × 100px</code> 的元素，带有 <code v-pre>2px</code> 的 <code v-pre>border</code> 和 <code v-pre>4px</code> 的 <code v-pre>padding</code> 将呈现为 <code v-pre>100px × 100px</code>，内部内容区域为 <code v-pre>88px × 88px</code>。</p>
<p>Tailwind 使默认的所有 <a href="https://tailwindcss.com/docs/preflight">preflight 里的元素</a> 遵循这个模型。</p>
<div class="box-border h-20 w-32 p-4 border-4 border-gray-400 bg-gray-200">
  <div class="h-full w-full bg-gray-400"></div>
</div>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">"box-border h-20 w-32 p-4 border-4 border-gray-400 bg-gray-200"</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">"h-full w-full bg-gray-400"</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<h2>排除边框和填充</h2>
<p>使用 <code v-pre>box-content</code> 将元素的 <code v-pre>box-size</code> 设置为 <code v-pre>content-box</code>，告诉浏览器在元素的指定宽度或高度上添加边框和填充。</p>
<p>这意味着一个 <code v-pre>100px × 100px</code> 的元素，带有 <code v-pre>2px</code> 的边框和 <code v-pre>4px</code> 的边距，实际上将呈现为 <code v-pre>112px × 112px</code>，内部内容区域为 <code v-pre>100px × 100px</code>。</p>
<div class="box-content h-20 w-32 p-4 border-4 border-gray-400 bg-gray-200">
  <div class="h-full w-full bg-gray-400"></div>
</div>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">"box-content h-20 w-32 p-4 border-4 border-gray-400 bg-gray-200"</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">"h-full w-full bg-gray-400"</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<h2>响应式</h2>
<p>若要控制特定断点处的框大小，请向任何现有的框样式添加 <code v-pre>{screen}:</code> 前缀。例如，使用 <code v-pre>md:box-content</code> 将 <code v-pre>box-content</code> 样式用于中等及以上屏幕尺寸。</p>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">"box-border md:box-content ..."</span>&gt;</span>
  <span class="hljs-comment">&lt;!-- ... --&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<p>想了解更多关于 Tailwind 的响应式设计，请查看<a href="https://tailwindcss.com/docs/responsive-design">响应式设计文档</a>。</p>
<h2>定制</h2>
<h3>响应式和伪类</h3>
<p>默认情况下，盒大小的样式可以和响应式变量一起使用。</p>
<p>您可以通过修改 <code v-pre>tailwind.config.js</code> 文件的 <code v-pre>variants</code> 部分中的 <code v-pre>boxSize</code> 属性来控制为 <code v-pre>box-sizing</code> 生成变量。</p>
<p>例如，这个配置也会生成 <code v-pre>hover</code> 和 <code v-pre>focus</code> :</p>
<pre v-pre><code v-pre class="language-js"> <span class="hljs-comment">// tailwind.config.js</span>
  <span class="hljs-built_in">module</span>.exports = {
    <span class="hljs-attr">variants</span>: {
      <span class="hljs-comment">// ...</span>
-     boxSizing: [<span class="hljs-string">'responsive'</span>],
+     boxSizing: [<span class="hljs-string">'responsive'</span>, <span class="hljs-string">'hover'</span>, <span class="hljs-string">'focus'</span>],
    }
  }
</code></pre>
<h2>禁用</h2>
<p>如果您不打算在项目中使用 <code v-pre>box-sizing</code>，可以通过在配置文件的 <code v-pre>correplugins</code> 部分将 <code v-pre>boxSizing</code> 属性设置为 <code v-pre>false</code> 来完全禁用它们:</p>
<pre v-pre><code v-pre class="language-js"><span class="hljs-comment">// tailwind.config.js</span>
  <span class="hljs-built_in">module</span>.exports = {
    <span class="hljs-attr">corePlugins</span>: {
      <span class="hljs-comment">// ...</span>
+     boxSizing: <span class="hljs-literal">false</span>,
    }
  }
</code></pre>
</section></template>

